import {
  appLanguageLabels,
  BROADCAST_BODY_MAX,
  BROADCAST_TITLE_MAX
} from "./broadcastStatus";
import {
  clean,
  IMergeResult
} from "./mergeBroadcastTranslations";
import { IBroadcastTranslationShape } from "../../interfaces/esim/BroadcastNotifications.types";

// Identifies the main message a set of translations was generated from: the source language plus
// the exact source text. If this changes, every existing translation is a translation of something
// the admin is no longer sending, so the next generate must refresh rather than skip.
//
// Deliberately includes the language: switching the source from English to Albanian invalidates the
// translations just as surely as editing the text does.
export const buildTranslationSignature = (
  sourceLanguage: string,
  title?: string | null,
  body?: string | null
): string => {
  return [
    (sourceLanguage || "").trim().toLowerCase(),
    (title || "").trim(),
    (body || "").trim()
  ].join(" ");
};

export interface IGenerationOutcome {
  success: string;
  problems: string[];
}

// Turns a merge result into the messages shown to the admin. Pure so the reporting can be checked
// without a form or a network call — partial success is the normal case here, and a run that
// quietly reports "done" while three languages came back empty is the failure worth guarding.
export const describeGenerationOutcome = (
  result: IMergeResult,
  regenerated: boolean
): IGenerationOutcome => {
  const count = result.appliedCodes.length;

  const success = regenerated
    ? `U rigjeneruan përkthimet për ${count} gjuhë`
    : `U gjeneruan përkthimet për ${count} gjuhë`;

  const problems: string[] = [];

  if (result.missingCodes.length) {
    problems.push(`Nuk u gjenerua: ${appLanguageLabels(result.missingCodes)}`);
  }

  if (result.tooLongCodes.length) {
    problems.push(
      `Teksti e kalon gjatësinë e lejuar për: ${appLanguageLabels(result.tooLongCodes)}`
    );
  }

  return { success, problems };
};

// Null when the main message can be translated; otherwise the reason, ready to show. Mirrors the
// backend's AITranslationRules.ValidateRequest: a message, not a throw. Needed on the client
// because this form only validates on submit, and because the backend DTO caps the same lengths —
// sending an over-long source would just come back a 400.
export const validateBroadcastSource = (
  title: string,
  body: string
): string | null => {
  if (!title || !body) {
    return "Plotësoni titullin dhe përmbajtjen e mesazhit kryesor para gjenerimit";
  }

  if (title.length > BROADCAST_TITLE_MAX || body.length > BROADCAST_BODY_MAX) {
    return (
      `Titulli deri në ${BROADCAST_TITLE_MAX} dhe përmbajtja deri në ` +
      `${BROADCAST_BODY_MAX} karaktere para gjenerimit`
    );
  }

  return null;
};

// Indexes of translation rows whose language is the main message's own language.
//
// Such a row must never exist: the source dropdown declares what language the main message is
// written in, so a translation box for that same language contradicts the declaration and shows
// the admin the same text twice. It can only arise by changing the source to a language that was
// already generated, which is exactly the case this finds.
export const sourceLanguageRowIndexes = (
  current: IBroadcastTranslationShape[],
  sourceLanguage: string
): number[] => {
  const source = clean(sourceLanguage).toLowerCase();

  return (current || []).reduce<number[]>((found, row, index) => {
    if (source && clean(row.applicationLanguage).toLowerCase() === source) {
      found.push(index);
    }
    return found;
  }, []);
};
